import { useState, useEffect, useRef, useMemo } from "react";
import { useTheme } from '@mui/material/styles';
import { useNavigate } from "react-router-dom";
import { Box, Grid, IconButton, TextField, Tooltip, tooltipClasses, Typography } from "@mui/material"
import { ButtonBasic, TableBasePaginado, TableLoadingComponent } from "../../components"
import ContactosService from "../../services/contactos.service";
import FechaFunction from "../../function/FechaFunction";
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import { styled } from '@mui/material/styles';
import { showModal } from "react-redux-modal-provider";
import ModalNewServicioSC from "../../modals/gobierno/ModalNewServicioSC";
import usePermisos from "../../hooks/usePermisos";
import SearchIcon from '@mui/icons-material/Search';

const DynamicTooltip = styled(({ className, ...props }) => (
    <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: theme.palette.mode === 'dark' ? '#2d3a52' : theme.palette.common.white,
        color: theme.palette.mode === 'dark' ? '#e0e0e0' : 'rgba(0, 0, 0, 0.87)',
        border: theme.palette.mode === 'dark' ? '1px solid #3d4a62' : 'none',
        boxShadow: theme.shadows[1],
        fontSize: 12,
    },
}));
const PotencialesGobiernoScreen = () => {
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === 'dark';
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [count, setCount] = useState(0);
    const [textSearch, setTextSearch] = useState('');
    const isInitialLoad = useRef(true);
    const searchTimeout = useRef(null);
    const navigate = useNavigate();
    const permisos = usePermisos()

    const actualizar = async (nroPagina = 1, limite = rowsPerPage, busqueda = textSearch) => {
        setLoading(true);
        await ContactosService.getAll({ type: 'gobierno', page: nroPagina, limit: limite, textSearch: busqueda }).then((r)=>{
            setItems(r?.data ?? r)
            setCount(r?.total ?? (r?.data ?? r)?.length ?? 0)
        }).finally(()=>setLoading(false));
    };
    const onChangePage = (nuevaPagina) => {
        setPage(nuevaPagina);
        actualizar(nuevaPagina + 1, rowsPerPage);
    };
    const onChangeRowsPerPage = (nuevoLimite) => {
        setRowsPerPage(nuevoLimite);
        setPage(0);
        actualizar(1, nuevoLimite);
    };
    const onClickNuevo = () => {
        navigate('/contactos/nuevogobierno');
    };
    const onClickSCNew = () => {
        showModal(ModalNewServicioSC)
    }
    const onOpenVer = (idCliente) => {
        const item = items.find((it)=> it.id_c === idCliente)
        if(item?.cuentaNubi_c){
            return navigate(`/contactos/${item?.cuentaNubi_c}/cuenta`,{state:{item:item}});
        }
        if(item?.nubicom){
            navigate(`/contactos/${item.id_c}/nubicom`,{state:{item:item}});
        }else if(item?.strong){
            navigate(`/contactos/${item.id_c}/strong`,{state:{item:item}});
        }else if(item?.ids){
            navigate(`/contactos/${item.id_c}/ids`,{state:{item:item}});
        }
        return ''
    }
    const columns = [
        {
            name: "id_c",
            label: "ID",
            options: {
                filter: false,
                sort: false,
                display:false,
                viewColumns: false
            }
        },
        {
            name: "createdAt",
            label: "Registrado",
            options: {
                filter: true,
                sort: true,
                customBodyRender: (value, tableMeta, updateValue) => {
                    return FechaFunction.format(value,'dd/MM/yyyy');
                }
            }
        },
        {
            label: "Nombre",
            name: "nombreCompleto_c",
            options: {
                filter: true,
                sort: true,
            }
        },
        {
            name: 'tipoDoc_c',
            label: "Tipo Documento",
            options: {
                filter: false,
                sort: false,
                customBodyRender: (value, tableMeta, updateValue) => {
                    if(value === '96'){
                        return 'DNI';
                    }
                    if(value === '80'){
                        return 'CUIT';
                    }
                    return 'Sin Dato'
                }
            }
        },
        {
            label: 'DNI',
            name: 'documento_c',
        }
        ,{
            label: 'Correo',
            name: 'email_c',
        }
        ,{
            label: 'Categoria',
            name: 'categoria_cxe',
        }
        ,{
            label: 'Estado',
            name: 'estadoCliente_c',
        },
        {
            name: 'actions',
            label: "Acciones",
            options: {
                filter: false,
                sort: false,
                customBodyRender: (value, tableMeta, updateValue) => (
                    <>
                        <Box display='flex'>
                            <DynamicTooltip  title="Perfil del Contacto" placement="top">
                                <IconButton color='primary' aria-label="ver" onClick={()=>onOpenVer(tableMeta.rowData[0])}>
                                    <AssignmentIndIcon fontSize="medium" sx={{color: isDarkMode ? '#90caf9' : '#012a6b'}} />
                                </IconButton>
                            </DynamicTooltip>
                        </Box>
                    </>
                )
            }
        }
    ];
    useEffect(() => {
        actualizar(1, rowsPerPage);
        document.title = `Potenciales Gobierno`;
    }, [])

    useEffect(() => {
        if(isInitialLoad.current){
            isInitialLoad.current = false;
            return;
        }
        if(searchTimeout.current) clearTimeout(searchTimeout.current);
        searchTimeout.current = setTimeout(() => {
            setPage(0);
            actualizar(1, rowsPerPage, textSearch);
        }, 500);
        return () => clearTimeout(searchTimeout.current);
    }, [textSearch])
    const styleInput = useMemo(() => ({
        '& .MuiInputLabel-root':{
            color: isDarkMode ? '#e0e0e0' : '#000',
            borderRadius:20,
            fontSize: 15,
        },
        '& fieldset': {
            borderRadius: 2,
            border: 1,
            borderRadius:20,
            borderColor: isDarkMode ? '#2d3a52' : '#fff',
        },
        '& .MuiOutlinedInput-root': {
            '&.Mui-focused fieldset': {
                border:1,
                borderRadius:20,
                color: isDarkMode ? '#e0e0e0' : '#000',
                borderColor: isDarkMode ? '#64B5F6' : '#fff'
            },
            '&:hover':{
                border: 1,
                borderRadius:20,
                background: isDarkMode ? '#2d3a52' : '#fff',
                borderColor: isDarkMode ? '#2d3a52' : '#fff',
                boxShadow: 3
            },
            '&.Mui-focused': {
                '&:hover':{
                    background: isDarkMode ? '#2d3a52' : '#fff',
                    borderRadius:20,
                    border:0
                }
            },
            background: isDarkMode ? '#1F2A40' : '#fff',
            color: isDarkMode ? '#e0e0e0' : '#000',
            borderRadius:20,
            height: 35,
            boxShadow: 4
        }
    }), [isDarkMode])

    return (
        <Box sx={{width:'100%',height: '100%',overflow:'auto', background: isDarkMode ? '#1a2332' : '#f5f5f5'}} className='side'>
            <Grid container p={4}>
                <Grid item xs={12} md={6} lg={6}>
                    <Typography variant="h2" sx={{color: isDarkMode ? '#e0e0e0' : '#111827', fontWeight: 'bold'}}>
                        Potenciales Clientes Gobierno/Eventuales
                    </Typography>
                </Grid>
                <Grid item xs={12} md={3} lg={3} sx={{display: 'flex', justifyContent: 'end'}}>
                    {permisos['trato.create.sc'] && (
                        <ButtonBasic
                            textButton="NUEVO SC"
                            sx={{width: 200, height: 30, color: '#fff',backgroundColor: '#01704a', '&:hover':{backgroundColor: '#02b879'}}}
                            onClick={()=>onClickSCNew()}
                            variant='wk'
                            color='nextStep'
                        />
                    )}
                </Grid>
                <Grid item xs={12} md={3} lg={3} sx={{display: 'flex', justifyContent: 'center'}}>
                    {permisos['contactos.gobierno.created'] && (
                        <ButtonBasic
                            textButton="NUEVO"
                            sx={{width: 200, height: 30, color: '#fff'}}
                            onClick={()=>onClickNuevo('nubi')}
                            variant='wk'
                            color='nextStep'
                        />
                    )}
                </Grid>
                <Grid item xs={12} md={6} lg={6} mt={2} mb={2}>
                    <TextField
                        size="small"
                        fullWidth
                        placeholder="Buscar por nombre, documento o celular"
                        value={textSearch}
                        sx={styleInput}
                        onChange={(e)=>setTextSearch(e.target.value)}
                        InputProps={{
                          startAdornment: <SearchIcon sx={{ mr: 1, color: isDarkMode ? '#90caf9' : '#012a6b' }} />
                        }}
                    />
                </Grid>
                <Grid item xs={12} sx={{marginBottom: 15}}>
                    {loading ? (
                        <TableLoadingComponent />
                    ) : (
                        <TableBasePaginado
                            title='Resultados'
                            columns={columns}
                            data={items}
                            count={count}
                            page={page}
                            rowsPerPage={rowsPerPage}
                            onChangePage={onChangePage}
                            onChangeRowsPerPage={onChangeRowsPerPage}
                        />
                    )}
                </Grid>
            </Grid>
        </Box>
    )
}

export default PotencialesGobiernoScreen